import {Trans} from '@lingui/macro';
import {isUndefined} from 'lodash';
import React, {FC, useCallback, useRef, useState} from 'react';

import {HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {SimpleDropdown} from '../../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {useUserTheme} from '../../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {ChannelModel} from '../../../../../../models/api/channelModel';
import {LoadedMessageModel, MessageVisualStatusesEnum} from '../../../../../../models/api/messageModel';
import {OkCancelDialog} from '../../../../../common/dialogs/okCancelDialog';
import {ChannelDropdownItem} from '../../../../composer/channelPicker/channelDropdownItem';
import {MessageViewerHeaderButton} from '../messageViewerHeaderButton';

/*
 * Props.
 */

export interface MessageViewerTweetRetweetButtonProps {
  message: LoadedMessageModel;
  twitterChannels: ReadonlyArray<ChannelModel>;
  onRetweetClick: HandlerOf<number>;
  isDisabled?: boolean;
}

/*
 * Component.
 */

export const MessageViewerTweetRetweetButton: FC<MessageViewerTweetRetweetButtonProps> = (props) => {
  const theme = useUserTheme();
  const {message, isDisabled, twitterChannels, onRetweetClick} = props;

  const buttonRef = useRef<HTMLDivElement>(null);
  const [isChannelPickerOpen, setIsChannelPickerOpen] = useState(false);
  const [retweetChannelId, setRetweetChannelId] = useState<number>();

  /*
   * Event handlers.
   */

  const onRetweetButtonClick = useCallback(() => {
    // If there are no channels, nothing to do.
    if (twitterChannels.length === 0) {
      return;
    }

    // If there are multiple channels, ask the user.
    if (twitterChannels.length > 1) {
      setIsChannelPickerOpen(true);
      return;
    }

    // Otherwise, ask for confirmation.
    const onlyChannel = twitterChannels[0];
    setRetweetChannelId(onlyChannel.id);
  }, [twitterChannels]);

  const onRetweetConfirmClick = useCallback(
    (selectedRetweetChannelId: number) => {
      // Reset state.
      setRetweetChannelId(undefined);

      // Notify parent.
      onRetweetClick(selectedRetweetChannelId);
    },
    [onRetweetClick],
  );

  /*
   * Render.
   */

  const renderButton = () => {
    const isRetweeted =
      message.visualStatus === MessageVisualStatusesEnum.TIMELINE &&
      message.twitterExtra &&
      message.twitterExtra.isRetweeted;

    if (isRetweeted) {
      return (
        <MessageViewerHeaderButton
          iconName="retweeted"
          label={<Trans id="message-tweet-retweeted">Retweeted</Trans>}
          color={theme.palette.green.shade40}
          hoverColor={theme.palette.green.shade50}
        />
      );
    }

    return (
      <MessageViewerHeaderButton
        ref={buttonRef}
        iconName="retweet"
        label={<Trans id="message-tweet-retweet">Retweet</Trans>}
        onClick={onRetweetButtonClick}
        isDisabled={isDisabled || twitterChannels.length === 0}
        hasHover={isChannelPickerOpen}
        color={theme.greys.shade80}
        hoverColor={theme.greys.shade80}
      />
    );
  };

  const renderChannelPicker = () => (
    <SimpleDropdown anchor={buttonRef} onRequestClose={() => setIsChannelPickerOpen(false)}>
      {twitterChannels.map((channel, index) => (
        <ChannelDropdownItem
          key={channel.id}
          index={index}
          currentChannel={undefined}
          channel={channel}
          onSelect={() => setRetweetChannelId(channel.id)}
        />
      ))}
    </SimpleDropdown>
  );

  const renderConfirmation = (selectedRetweetChannel: ChannelModel) => (
    <OkCancelDialog
      title={<Trans id="message-retweet-title">Retweet with {selectedRetweetChannel.displayName}?</Trans>}
      okButtonLabel={<Trans id="message-retweet-ok">Retweet</Trans>}
      onOkClick={() => onRetweetConfirmClick(selectedRetweetChannel.id)}
      onRequestClose={() => setRetweetChannelId(undefined)}
    >
      {message.blurb}
    </OkCancelDialog>
  );

  const retweetChannel = !isUndefined(retweetChannelId)
    ? twitterChannels.find((channel) => channel.id === retweetChannelId)
    : undefined;

  return (
    <>
      {renderButton()}
      {isChannelPickerOpen && renderChannelPicker()}
      {retweetChannel && renderConfirmation(retweetChannel)}
    </>
  );
};
